import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { SERVICES } from "../../lib/data";
import { Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const initial = { name: "", company: "", phone: "", email: "", service: "", message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in name, phone, email and service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received! Our team will contact you shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try calling us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="q-name" className="overline text-zinc-500">Full Name *</Label>
          <Input id="q-name" data-testid="quote-name-input" value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your name" className="rounded-none bg-zinc-50 border-zinc-300 h-12 focus-visible:ring-accent" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="q-company" className="overline text-zinc-500">Company</Label>
          <Input id="q-company" data-testid="quote-company-input" value={form.company}
            onChange={(e) => update("company", e.target.value)}
            placeholder="Company / Organization" className="rounded-none bg-zinc-50 border-zinc-300 h-12 focus-visible:ring-accent" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="q-phone" className="overline text-zinc-500">Phone *</Label>
          <Input id="q-phone" data-testid="quote-phone-input" value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+91 ..." className="rounded-none bg-zinc-50 border-zinc-300 h-12 focus-visible:ring-accent" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="q-email" className="overline text-zinc-500">Email *</Label>
          <Input id="q-email" type="email" data-testid="quote-email-input" value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@company.com" className="rounded-none bg-zinc-50 border-zinc-300 h-12 focus-visible:ring-accent" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="overline text-zinc-500">Service Required *</Label>
        <Select value={form.service} onValueChange={(v) => update("service", v)}>
          <SelectTrigger data-testid="quote-service-select" className="rounded-none bg-zinc-50 border-zinc-300 h-12 focus:ring-accent">
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s.id} value={s.title} data-testid={`service-option-${s.id}`}>{s.title}</SelectItem>
            ))}
            <SelectItem value="Other / General Enquiry">Other / General Enquiry</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="q-msg" className="overline text-zinc-500">Project Details</Label>
        <Textarea id="q-msg" data-testid="quote-message-input" value={form.message}
          onChange={(e) => update("message", e.target.value)} rows={4}
          placeholder="Tell us about your project, site location and scope..."
          className="rounded-none bg-zinc-50 border-zinc-300 focus-visible:ring-accent" />
      </div>
      <Button type="submit" disabled={loading} data-testid="quote-submit-btn"
        className="rounded-none h-12 px-8 bg-accent hover:bg-accent/90 text-white font-semibold w-full sm:w-auto group">
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Request a Quote
        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
      </Button>
    </form>
  );
};
